import { createSlice } from "@reduxjs/toolkit"
import waterAPI from "./waterAPI"
import { createAppAsyncThunk } from "app/hooks"
import { IWaterState, IDailyWater } from "./waterSlice.types"
import { resetStoreAction } from "config"

let getDailyWaterAbortController: AbortController | null = null

export const getDailyWater = createAppAsyncThunk(
  "water/getDailyWater",
  async (date: string, { rejectWithValue }) => {
    if (getDailyWaterAbortController) {
      getDailyWaterAbortController.abort()
    }

    const localController = new AbortController()
    getDailyWaterAbortController = localController

    try {
      const response = await waterAPI.getDailyWater(
        date,
        localController.signal,
      )
      return response
    } catch (err: any) {
      if (localController.signal.aborted) {
        return rejectWithValue({ aborted: true })
      }
      return rejectWithValue(err)
    } finally {
      if (getDailyWaterAbortController === localController) {
        getDailyWaterAbortController = null
      }
    }
  },
)

export const logWater = createAppAsyncThunk(
  "water/logWater",
  async (payload: IDailyWater, { rejectWithValue }) => {
    try {
      await waterAPI.addWater(payload)
      return payload
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const updateWater = createAppAsyncThunk(
  "water/updateWater",
  async (payload: IDailyWater, { rejectWithValue }) => {
    try {
      await waterAPI.updateWater(payload)
      return payload
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

const initialState: IWaterState = {
  logWaterLoading: false,
  logWaterSuccess: undefined,
  logWaterFailed: undefined,

  updateWaterLoading: false,
  updateWaterSuccess: undefined,
  updateWaterFailed: undefined,

  dailyWaterTotal: 0,
  getDailyWaterLoading: false,
  getDailyWaterFailed: undefined,
}

export const waterSlice = createSlice({
  name: "water",
  initialState,
  reducers: {
    resetWaterState: () => initialState,
    clearLogWaterSuccess: (state) => {
      state.logWaterSuccess = undefined
    },
    clearUpdateWaterSuccess: (state) => {
      state.updateWaterSuccess = undefined
    },
  },
  extraReducers(builder) {
    builder
      .addCase(logWater.pending, (state) => {
        state.logWaterLoading = true
        state.logWaterFailed = undefined
      })
      .addCase(logWater.fulfilled, (state, { payload }) => {
        state.logWaterLoading = false
        state.logWaterSuccess = payload
        state.dailyWaterTotal += payload.amount
      })
      .addCase(logWater.rejected, (state, { payload }) => {
        state.logWaterLoading = false
        state.logWaterFailed = payload
      })

      .addCase(updateWater.pending, (state) => {
        state.updateWaterLoading = true
        state.updateWaterFailed = undefined
      })
      .addCase(updateWater.fulfilled, (state, { payload }) => {
        state.updateWaterLoading = false
        state.updateWaterSuccess = payload
        state.dailyWaterTotal = payload.amount
      })
      .addCase(updateWater.rejected, (state, { payload }) => {
        state.updateWaterLoading = false
        state.updateWaterFailed = payload
      })

      .addCase(getDailyWater.pending, (state) => {
        state.getDailyWaterLoading = true
        state.getDailyWaterFailed = undefined
      })
      .addCase(getDailyWater.fulfilled, (state, { payload }) => {
        state.getDailyWaterLoading = false
        state.dailyWaterTotal = payload.amount
      })
      .addCase(getDailyWater.rejected, (state, { payload }) => {
        if ((payload as any)?.aborted) {
          return
        }
        state.getDailyWaterLoading = false
        state.getDailyWaterFailed = payload
      })

      .addCase(resetStoreAction, () => initialState)
  },
})

export const {
  resetWaterState,
  clearLogWaterSuccess,
  clearUpdateWaterSuccess,
} = waterSlice.actions

export default waterSlice.reducer
